import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminRelease, AdminGroup } from '@/lib/api'
import RowActions from '@/components/RowActions'
import { useConfirm } from '@/components/ConfirmDialog'
import { normalizeImageKitUrl } from '@lic/media-urls'

function ReleaseCard({
  release,
  onView,
  onEdit,
  onDelete,
}: {
  release: AdminRelease
  onView: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div
      className="relative rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-700 cursor-pointer transition-all group"
      onClick={onView}
    >
      <div className="aspect-square relative overflow-hidden bg-zinc-800">
        {release.image_url ? (
          <img
            src={normalizeImageKitUrl(release.image_url)}
            alt={release.alt || release.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-zinc-800">
            <span className="text-zinc-600 text-3xl font-bold">{release.title[0]}</span>
          </div>
        )}

        {/* Label badge — top left */}
        {release.label && (
          <div className="absolute top-2 left-2 z-10">
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full backdrop-blur-sm bg-black/30 text-white/70 border border-white/10 max-w-[120px] truncate block">
              {release.label}
            </span>
          </div>
        )}

        {/* RowActions — top right */}
        <div className="absolute top-1.5 right-1.5 z-10" onClick={(e) => e.stopPropagation()}>
          <RowActions
            triggerClassName="flex items-center justify-center w-7 h-7 rounded bg-black/20 hover:bg-black/40 backdrop-blur-sm text-white/80 hover:text-white transition-colors"
            actions={[
              { label: 'Edit', onClick: onEdit },
              { label: 'Hapus', onClick: onDelete, variant: 'danger' },
            ]}
          />
        </div>

        {/* Glassmorphism content overlay — bottom */}
        <div className="absolute inset-x-0 bottom-0 backdrop-blur-md bg-black/30 border-t border-white/10 px-3 py-2.5">
          <p className="text-white font-semibold text-sm leading-tight truncate">{release.title}</p>
          <p className="text-white/45 text-[11px] mt-0.5">{release.release_date}</p>
        </div>
      </div>
    </div>
  )
}

const CARD_GRID = 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3'

export default function DiscographyPage() {
  const { token, isGroupManager, managedGroupSlug } = useAuth()
  const navigate = useNavigate()

  const [releases, setReleases] = useState<AdminRelease[]>([])
  const [groups, setGroups] = useState<AdminGroup[]>([])
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { confirm, dialog } = useConfirm()

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      if (isGroupManager) {
        const releaseList = await api.getAdminDiscography(token)
        setReleases(releaseList)
      } else {
        const [releaseList, groupList] = await Promise.all([
          api.getAdminDiscography(token),
          api.getAdminGroups(token),
        ])
        setReleases(releaseList)
        setGroups(groupList)
        setExpandedGroups(new Set(groupList.map((g) => g.id)))
      }
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [token, isGroupManager])

  useEffect(() => { load() }, [load])

  const handleDelete = async (id: string, title: string) => {
    if (!await confirm(`"${title}" akan dihapus permanen dan tidak bisa dipulihkan.`, { title: `Hapus "${title}"` })) return
    if (!token) return
    try {
      await api.deleteDiscography(token, id)
      load()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    }
  }

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }

  const sortReleases = (list: AdminRelease[]) =>
    [...list].sort((a, b) => new Date(b.release_date).getTime() - new Date(a.release_date).getTime())

  if (loading) {
    return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  }

  if (error) {
    return <div className="p-6"><p className="text-red-400 text-sm">{error}</p></div>
  }

  /* ── Group Manager: flat card grid ─────────────────────────── */
  if (isGroupManager) {
    const myReleases = sortReleases(
      managedGroupSlug ? releases.filter((r) => r.group_slug === managedGroupSlug) : releases
    )

    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-lg font-semibold text-white">Discography</h1>
            <p className="text-zinc-500 text-xs mt-0.5">{myReleases.length} releases</p>
          </div>
          <button
            onClick={() => navigate('/discography/create')}
            className="bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold px-4 py-2 rounded transition-colors"
          >
            + Add Release
          </button>
        </div>

        {myReleases.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-zinc-500 text-sm">Belum ada release.</p>
            <button
              onClick={() => navigate('/discography/create')}
              className="mt-3 text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
            >
              + Tambah release pertama
            </button>
          </div>
        ) : (
          <div className={CARD_GRID}>
            {myReleases.map((release) => (
              <ReleaseCard
                key={release.id}
                release={release}
                onView={() => navigate(`/discography/${release.id}`, { state: { release } })}
                onEdit={() => navigate(`/discography/${release.id}/edit`, { state: { release } })}
                onDelete={() => handleDelete(release.id, release.title)}
              />
            ))}
          </div>
        )}
        {dialog}
      </div>
    )
  }

  /* ── Admin / Superadmin: accordion per group ───────────────── */
  const releasesByGroup: Record<string, AdminRelease[]> = {}
  for (const r of releases) {
    ;(releasesByGroup[r.group_slug] ??= []).push(r)
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-white">Discography</h1>
          <p className="text-zinc-500 text-xs mt-0.5">
            {releases.length} releases · {groups.length} groups
          </p>
        </div>
      </div>

      {groups.length === 0 && (
        <div className="py-16 text-center">
          <p className="text-zinc-500 text-sm">Belum ada group.</p>
        </div>
      )}

      <div className="space-y-6">
        {groups.map((group) => {
          const groupReleases = sortReleases(releasesByGroup[group.slug] ?? [])
          const isExpanded = expandedGroups.has(group.id)

          return (
            <div key={group.id}>
              {/* Accordion header */}
              <div className="flex items-center gap-3 mb-3">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  className="flex items-center gap-2.5 flex-1 min-w-0 text-left py-1 group/header"
                >
                  <div
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: group.color_primary || '#3f3f46' }}
                  />
                  <span className="font-semibold text-white text-sm group-hover/header:text-zinc-200 transition-colors">
                    {group.name}
                  </span>
                  <span className="text-zinc-600 text-xs flex-shrink-0">
                    {groupReleases.length} releases
                  </span>
                  <ChevronDown
                    size={14}
                    className={`text-zinc-500 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                  />
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/discography/create', { state: { initialGroupSlug: group.slug } })}
                  className="text-[11px] text-zinc-500 hover:text-yellow-400 transition-colors flex-shrink-0 px-2 py-1 rounded hover:bg-zinc-800"
                >
                  + Add
                </button>
              </div>

              <div className="h-px bg-zinc-800 mb-4" />

              {isExpanded && (
                groupReleases.length === 0 ? (
                  <p className="text-zinc-600 text-xs py-4 px-1">Belum ada release di group ini.</p>
                ) : (
                  <div className={CARD_GRID}>
                    {groupReleases.map((release) => (
                      <ReleaseCard
                        key={release.id}
                        release={release}
                        onView={() => navigate(`/discography/${release.id}`, { state: { release } })}
                        onEdit={() => navigate(`/discography/${release.id}/edit`, { state: { release } })}
                        onDelete={() => handleDelete(release.id, release.title)}
                      />
                    ))}
                  </div>
                )
              )}
            </div>
          )
        })}
      </div>

      {dialog}
    </div>
  )
}
